import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";
import { http, CALLBACK_TIMES, errMsg } from "@/lib/data";

const field = "w-full h-12 rounded-xl border border-slate-200 bg-white px-4 text-base outline-none focus:ring-2 focus:ring-[#10B981]";

export const LeadModal = ({ partner, onClose }) => {
  const [form, setForm] = useState({ name: "", phone: "", time: "" });
  const [state, setState] = useState({ loading: false, error: "", done: false });

  useEffect(() => {
    setForm({ name: "", phone: "", time: "" });
    setState({ loading: false, error: "", done: false });
  }, [partner]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.time) return setState((s) => ({ ...s, error: "Please choose a callback time" }));
    setState({ loading: true, error: "", done: false });
    try {
      await http.post("/leads", { client_name: form.name, client_phone: form.phone, target_partner_id: partner.id, callback_time: form.time });
      setState({ loading: false, error: "", done: true });
    } catch (err) {
      setState({ loading: false, error: errMsg(err), done: false });
    }
  };

  return (
    <Dialog open={!!partner} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lead-modal" className="bg-white rounded-2xl max-w-md w-[92vw]">
        {state.done ? (
          <div data-testid="lead-success" className="text-center py-6">
            <CheckCircle2 className="w-14 h-14 text-[#10B981] mx-auto" />
            <h3 className="font-display text-xl font-bold mt-3">You're connected!</h3>
            <p className="text-sm text-slate-600 mt-2">A DhanTrove verified expert will call you back about {partner?.company_name} at your preferred time.</p>
            <button data-testid="lead-success-close" onClick={onClose} className="mt-6 h-11 px-6 rounded-xl bg-[#1E293B] text-white font-semibold">Done</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Connect with {partner?.company_name}</DialogTitle>
              <DialogDescription>Share your details and get a callback. No spam, no login.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-2">
              <input data-testid="lead-modal-name-input" required minLength={2} placeholder="Your Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
              <div className="flex">
                <span className="h-12 px-3 inline-flex items-center rounded-l-xl border border-r-0 border-slate-200 bg-slate-50 text-slate-500 text-sm">+91</span>
                <input data-testid="lead-modal-phone-input" required inputMode="numeric" maxLength={10} placeholder="10-digit Mobile Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })} className={`${field} rounded-l-none`} />
              </div>
              <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })}>
                <SelectTrigger data-testid="lead-modal-time-select" className="h-12 rounded-xl text-base"><SelectValue placeholder="Preferred Callback Time" /></SelectTrigger>
                <SelectContent>
                  {CALLBACK_TIMES.map((t, i) => <SelectItem key={t} value={t} data-testid={`lead-time-option-${i}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              {state.error && <p data-testid="lead-modal-error" className="text-sm text-red-600">{state.error}</p>}
              <button data-testid="lead-modal-submit-button" disabled={state.loading} className="w-full h-12 rounded-xl bg-[#10B981] hover:bg-[#059669] text-white font-bold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-70">
                {state.loading && <Loader2 className="w-4 h-4 animate-spin" />} Request Callback
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
